/**
 * Either API
 */

export function error<TErr>(err: TErr): Either<TErr, any> {
  return create<TErr, any>(err, undefined);
}

export function result<TResult>(value: TResult): Either<any, TResult> {
  return create<any, TResult>(undefined, value);
}

function create<TErr, TResult>(
  err: TErr,
  // tslint:disable-next-line: no-shadowed-variable
  result: TResult
): Either<TErr, TResult> {
  const either: RawEither<TErr, TResult> = [err, result] as any;

  return {
    value() {
      return either[1];
    },
    error() {
      return either[0];
    },
    map<TMapped>(
      mapValue: (value: TResult) => TMapped,
      // tslint:disable-next-line: no-shadowed-variable
      mapError?: (value: any) => TMapped
    ): TMapped extends void ? Either<any, TResult> : Either<any, TMapped> {
      const mappedValue = map(either, mapValue, mapError);
      return mappedValue ? wrap(mappedValue) : (wrap(either) as any);
    },
    mapError<TMapped>(mapper: (value: any) => TMapped) {
      const mappedValue = mapError(either, mapper);
      return mappedValue ? wrap(mappedValue) : (wrap(either) as any);
    }
  };
}

function wrap<TLeft, TRight>(
  either: RawEither<TLeft, TRight>
): Either<TLeft, TRight> {
  return isLeft(either) ? error(either[0]) : (result(either[1]) as any);
}

export interface Either<TErr, TResult> {
  value(): TResult | undefined;
  error(): TErr | undefined;

  map<TMapped>(
    mapValue: (value: TResult) => TMapped,
    mapError?: (value: TErr) => TMapped
  ): TMapped extends void ? Either<TErr, TResult> : Either<TErr, TMapped>;
  mapError<TMapped>(
    mapError: (value: TErr) => TMapped
  ): TMapped extends void ? Either<TErr, TResult> : Either<TErr, TMapped>;
}

/**
 * Either implementations
 */

type RawEither<TLeft, TRight = never> = EitherLeft<TLeft> | EitherRight<TRight>;

type EitherLeft<TLeft> = [TLeft];
type EitherRight<TRight> = [undefined, TRight];

export function isLeft<TLeft, TRight>(
  either: RawEither<TLeft, TRight>
): either is [TLeft] {
  const [left] = either;

  return !!left;
}

export function isRight<TLeft, TRight>(
  either: RawEither<TLeft, TRight>
): either is [undefined, TRight] {
  const [left] = either;

  return !!left;
}

export function map<TLeft, TRight, TResult>(
  either: RawEither<TLeft, TRight>,
  mapRight: (v: TRight) => TResult,
  mapLeft?: (v: TLeft) => TResult
): RawEither<TLeft, TResult> {
  return isLeft(either)
    ? mapLeft
      ? ([, mapLeft(either[0])] as any)
      : (either as any)
    : ([, mapRight(either[1])] as any);
}

function mapError<TErr, TResult, TMapped>(
  either: RawEither<TErr, TResult>,
  mapper: (err: TErr) => TMapped
): RawEither<TErr, TMapped> {
  const [err] = either;

  return isLeft(either) ? [, mapper(err!)] : (either as any);
}
